import React from 'react';
import { createRoot } from 'react-dom/client';
import { BookingBarForm, FieldConfig, RowConfig } from './components/BookingBarForm';
import { getQueryParam } from '../../_shared/oceanSearchContext';

// WP's block "layout" support (see ocean/booking-bar-row's block.json)
// bakes the editor's chosen alignment into an `is-content-justification-*`
// class on the row's own saved wrapper element -- there's no attribute or
// data-* marker for it, so it has to be read off the class list like this,
// same DOM-reading approach as the field configs below. Mirrors the CSS
// wp-includes ships for these classes (block-library/style.css).
const JUSTIFY_CONTENT_BY_CLASS: Record<string, string> = {
	'is-content-justification-left': 'flex-start',
	'is-content-justification-right': 'flex-end',
	'is-content-justification-center': 'center',
	'is-content-justification-space-between': 'space-between',
	'is-content-justification-stretch': 'stretch',
	};

const readRowJustifyContent = (rowEl: HTMLElement): string | undefined => {
	const match = Object.keys(JUSTIFY_CONTENT_BY_CLASS).find((className) => rowEl.classList.contains(className));
	return match ? JUSTIFY_CONTENT_BY_CLASS[match] : undefined;
	};

// The editor only builds structure (two ocean/booking-bar-row blocks, each
// containing whichever ocean/booking-bar-field-* blocks the editor placed,
// in DOM order) -- see each field block's save() for the specific
// data-field-type/data-* markers read here. The real interactive form is
// entirely assembled by this hydration step from that structure, not by any
// of the field blocks having frontend scripts of their own.
// The direct field children of `container` -- a row, or (recursing) a
// more-filters field, which is itself a container of filter fields. Only
// `:scope >` matches, so a more-filters panel's own fields are read once, as
// that field's children, rather than a second time as the row's.
const readFields = (container: HTMLElement): FieldConfig[] => {
	const fieldEls = Array.from(container.querySelectorAll<HTMLElement>(':scope > [data-field-type]'));

	return fieldEls
		.map((fieldEl): FieldConfig | null => {
			switch (fieldEl.dataset.fieldType) {
				case 'dates':
					return { type: 'dates' };
				case 'guests':
					return {
						type: 'guests',
						min: parseInt(fieldEl.dataset.minGuests ?? '1', 10) || 1,
						max: parseInt(fieldEl.dataset.maxGuests ?? '20', 10) || 20,
						};
				case 'location':
					return { type: 'location' };
				case 'feature':
					return fieldEl.dataset.featureSlug ? { type: 'feature', slug: fieldEl.dataset.featureSlug } : null;
				case 'submit':
					return { type: 'submit', buttonText: fieldEl.dataset.buttonText || 'Search' };
				case 'more-filters':
					return {
						type: 'more-filters',
						buttonText: fieldEl.dataset.buttonText || 'More filters',
						fields: readFields(fieldEl),
						};
				default:
					return null;
				}
			})
		.filter((field): field is FieldConfig => field !== null);
	};

const readRowsConfig = (container: HTMLElement): RowConfig[] => {
	const rowEls = Array.from(container.querySelectorAll<HTMLElement>(':scope > .wp-block-ocean-booking-bar-row'));

	return rowEls.map((rowEl) => ({ fields: readFields(rowEl), justifyContent: readRowJustifyContent(rowEl) }));
	};

// Sticky ("Stick to bottom on scroll", the block's `sticky` attribute --
// saved as an is-sticky class on the block wrapper, see edit.tsx's BlockSave).
//
// position:fixed rather than position:sticky, because on the homepage this
// block lives inside the hero core/cover: a sticky element can only travel
// within its own ancestor's box, so it would unstick and scroll away the
// moment the hero did. Fixed leaves the flow entirely, so a sentinel inserted
// in the bar's place holds the gap open and doubles as the measurement of
// where the bar belongs when unpinned -- no cached offsetTop to go stale if
// anything above the bar changes height (the collapsing header, a lazy image).
//
// It pins to the BOTTOM of the viewport (40px up -- that offset lives purely
// in the stylesheet, nothing here needs to know it).
//
// The bar stays put in the flow until it would scroll up out of sight behind
// the fixed header, and only then reappears as a floating bottom bar. The
// obvious alternative -- detaching as soon as its in-flow position rises above
// the bottom slot -- meant a bar sitting in the homepage hero was already
// above that slot at scroll zero, so it pinned instantly on load and dropped
// to the bottom of the screen before the visitor had scrolled at all.
//
// Hence the one bit of header coupling: the detach point is .site-header's
// rendered height (scroll-linked, see jrx/fe/src/header.ts), read live.
// offsetHeight rather than getBoundingClientRect().bottom, so the header's own
// slide-up (.is-hidden, while scrolling down) doesn't drag the threshold with
// it and flip the bar back and forth.
// The bar's gap above the viewport bottom, owned by the stylesheet
// (--ocean-bookingbar-sticky-bottom) -- CSS positions the pinned bar with it,
// this needs the same number to reserve a matching gap above the footer and to
// know when the footer has reached the bar.
const STICKY_BOTTOM_FALLBACK_PX = 40;

const stickyBottom = (container: HTMLElement): number => {
	const declared = parseFloat(getComputedStyle(container).getPropertyValue('--ocean-bookingbar-sticky-bottom'));
	return Number.isFinite(declared) ? declared : STICKY_BOTTOM_FALLBACK_PX;
	};

// Whether the bar may detach at all right now -- driven from the stylesheet
// (--ocean-bookingbar-pins, 0 below the theme's mobile breakpoint) so the
// breakpoint itself lives in one place. Re-read on every update rather than
// cached, which is what makes a resize across it just work.
const pinningAllowed = (container: HTMLElement): boolean =>
	getComputedStyle(container).getPropertyValue('--ocean-bookingbar-pins').trim() !== '0';

const detachPoint = (): number => {
	const header = document.querySelector<HTMLElement>('.site-header');
	if (!header)
		return 0;
	const adminBar = document.getElementById('wpadminbar');
	return header.offsetHeight + (adminBar ? adminBar.offsetHeight : 0);
	};

const initSticky = (container: HTMLElement) => {
	const sentinel = document.createElement('div');
	sentinel.className = 'booking-bar-sentinel';
	container.parentNode?.insertBefore(sentinel, container);

	let pinned = false;
	let ticking = false;

	// The vertical space the block actually occupies in the flow, which is NOT
	// the section's own height: .booking-bar's `margin: 2rem auto` collapses
	// straight through this section (it has no padding or border of its own),
	// so those 2rem sit outside the section's box while still taking up room
	// on the page. Freezing only the section's height into the sentinel let
	// the page jump 4rem at the moment of pinning.
	const flowHeight = (): number => {
		const bar = container.querySelector<HTMLElement>('.booking-bar');
		const height = container.getBoundingClientRect().height;
		if (!bar)
			return height;
		const style = getComputedStyle(bar);
		return height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
		};

	// The main footer block, which the pinned bar would otherwise sit on top of
	// once the page is scrolled to the bottom. Two halves to that fix: reserve
	// a gap above it the size of the bar (below), and stop the bar entering the
	// footer at all (the clamp in update()) -- so at full scroll the bar parks
	// in that reserved gap rather than floating over the footer's content.
	const footer = document.querySelector<HTMLElement>('.footer-grid');

	// Bar height plus its bottom gap twice over -- the same clearance above the
	// bar as below it. Published on <html> rather than set on the footer
	// directly so the stylesheet decides what to do with it (see
	// --ocean-bookingbar-reserve in booking-bar.scss).
	const setReserve = (value: number) => {
		document.documentElement.style.setProperty('--ocean-bookingbar-reserve', `${value}px`);
		};

	const unpin = () => {
		container.classList.remove('is-pinned');
		container.style.transform = '';
		sentinel.style.height = '';
		pinned = false;
		};

	const update = () => {
		ticking = false;

		// Mobile: never detach. Also drops an already-pinned bar back into the
		// flow, for a desktop window narrowed while the bar is floating, and
		// gives the footer its gap back -- nothing is going to float over it.
		if (!pinningAllowed(container)) {
			if (pinned)
				unpin();
			setReserve(0);
			return;
			}

		const gap = stickyBottom(container);
		setReserve(container.getBoundingClientRect().height + gap * 2);

		// Where the bar sits (or would sit) in the flow -- the sentinel holds
		// that spot once the bar itself is fixed, so this stays meaningful in
		// both states and never needs a cached offsetTop that could go stale.
		const naturalTop = sentinel.getBoundingClientRect().top;
		const threshold = detachPoint();

		if (!pinned && naturalTop <= threshold) {
			// Measured before the bar leaves the flow.
			sentinel.style.height = `${flowHeight()}px`;
			container.classList.add('is-pinned');
			pinned = true;
			}
		else if (pinned && naturalTop > threshold) {
			unpin();
			}

		// Park the bar above the footer instead of over it. `bottom` in the
		// stylesheet holds it a fixed distance off the viewport floor; once the
		// footer has risen past that line, this lifts the bar by however far it
		// has intruded, so the bar comes to rest in the gap reserved above the
		// footer and travels with it from there.
		//
		// The line it stops at is `gap` ABOVE the footer's top, not the footer's
		// top itself: the reserve above is height + gap*2, so leaving a gap below
		// the bar is what leaves a matching gap above it and centres the bar in
		// the space reserved for it. Clamping to the footer's own top edge parked
		// it flush on the footer with all the slack stacked above.
		if (pinned && footer) {
			const intrusion = window.innerHeight - gap - (footer.getBoundingClientRect().top - gap);
			container.style.transform = intrusion > 0 ? `translateY(${-intrusion}px)` : '';
			}
		};

	const onScroll = () => {
		if (ticking)
			return;
		ticking = true;
		window.requestAnimationFrame(update);
		};

	// Deferred a frame rather than calling update() straight away -- the React
	// root above hasn't painted yet at this point, so the container's height
	// (frozen into the sentinel on pin) would measure as 0. Matters only for a
	// page loaded already scrolled past the bar, e.g. a mid-page refresh.
	onScroll();
	window.addEventListener('scroll', onScroll, { passive: true });
	window.addEventListener('resize', onScroll, { passive: true });
	};

// Hydrate all booking bar blocks on the page
document.addEventListener('DOMContentLoaded', () => {
	const containers = document.querySelectorAll<HTMLElement>('.wp-block-ocean-booking-bar');

	containers.forEach((container) => {
		const backgroundOpacity = parseFloat(container.dataset.backgroundOpacity ?? '0.62');
		const rows = readRowsConfig(container);

		const locations = window.oceanSearchData?.locations || [];
		const lockedScope = window.oceanSearchData?.lockedScope || null;
		const features = window.oceanSearchData?.features || [];

		// Get initial values from URL query params
		const initialLocation = getQueryParam('location');
		const initialCheckIn = getQueryParam('check_in');
		const initialCheckOut = getQueryParam('check_out');
		const initialGuests = parseInt(getQueryParam('guests')) || 1;
		const initialFeatures = getQueryParam('amenities').split(',').filter(Boolean);

		const root = createRoot(container);
		root.render(
			<BookingBarForm
				rows={rows}
				backgroundOpacity={backgroundOpacity}
				locations={locations}
				lockedScope={lockedScope}
				initialLocation={initialLocation}
				initialCheckIn={initialCheckIn}
				initialCheckOut={initialCheckOut}
				initialGuests={initialGuests}
				features={features}
				initialFeatures={initialFeatures}
			/>
		);

		if (container.classList.contains('is-sticky')) {
			initSticky(container);
			}
		});
	});
